import { useCallback, useEffect, useMemo, useState } from 'react'
import { BarChart3, RefreshCw } from 'lucide-react'
import { fetchAdminStats } from '../api/client'
import type { AdminStats } from '../api/types'
import { formatDisplayedUsd } from '../costDisplay'

type PresetKey = '1h' | '1d' | '1w' | 'all' | 'custom'

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: '1h', label: 'Son 1 Saat' },
  { key: '1d', label: 'Son 1 Gün' },
  { key: '1w', label: 'Son 1 Hafta' },
  { key: 'all', label: 'Tümü' },
  { key: 'custom', label: 'Özel' },
]

const USAGE_LABELS: Record<string, string> = {
  PAPER_GRADING: 'Kağıt Notlandırma',
  RUBRIC_PROCESSING: 'Rubrik İşleme',
  QUESTION_EXTRACTION: 'Soru Çıkarma',
  CLASS_ANALYSIS: 'Sınıf Analizi',
  ADMIN_TEST: 'Admin AI Test',
}

function getRange(preset: PresetKey, customStart?: string, customEnd?: string) {
  if (preset === 'all') return { start: undefined, end: undefined }
  if (preset === 'custom') {
    if (!customStart && !customEnd) return { start: undefined, end: undefined }
    return {
      start: customStart ? new Date(customStart + 'T00:00:00Z').toISOString() : undefined,
      end: customEnd ? new Date(customEnd + 'T23:59:59Z').toISOString() : undefined,
    }
  }
  const now = Date.now()
  const end = new Date(now).toISOString()
  let start: string
  const ms = { '1h': 3600_000, '1d': 86_400_000, '1w': 604_800_000 }
  start = new Date(now - (ms[preset] ?? 86_400_000)).toISOString()
  return { start, end }
}

export default function AdminStatsPage() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [preset, setPreset] = useState<PresetKey>('1d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  const doFetch = useCallback((start?: string, end?: string) => {
    setStatus('loading')
    fetchAdminStats(start, end)
      .then((d) => { setStats(d); setStatus('ready') })
      .catch(() => setStatus('error'))
  }, [])

  useEffect(() => {
    if (preset === 'custom') return
    const { start, end } = getRange(preset)
    doFetch(start, end)
  }, [preset, doFetch])

  const handlePreset = (key: PresetKey) => {
    setPreset(key)
  }

  const handleCustomSearch = () => {
    const { start, end } = getRange('custom', customStart, customEnd)
    doFetch(start, end)
  }

  const todayLocal = useMemo(() => new Date().toISOString().slice(0, 10), [])

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-24">
        <div className="flex items-center gap-12">
          <BarChart3 size={22} />
          <h1 className="text-xl font-bold">İstatistikler</h1>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => { const { start, end } = getRange(preset, customStart, customEnd); doFetch(start, end) }} disabled={status === 'loading'}>
          <RefreshCw size={14} className={status === 'loading' ? 'spin' : ''} />
          Yenile
        </button>
      </div>

      <div className="flex items-center gap-8 mb-20 flex-wrap">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={`btn btn-sm ${preset === p.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => handlePreset(p.key)}
          >
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-8 ml-4">
            <input
              type="date"
              className="input input-sm"
              value={customStart}
              max={todayLocal}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span className="text-muted">–</span>
            <input
              type="date"
              className="input input-sm"
              value={customEnd}
              max={todayLocal}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
            <button className="btn btn-sm btn-primary" onClick={handleCustomSearch}>
              Ara
            </button>
          </div>
        )}
      </div>

      {status === 'error' && (
        <div className="card card-padding">
          <p className="text-muted">Veriler yüklenemedi.</p>
        </div>
      )}

      {status === 'loading' && !stats && (
        <div className="card card-padding">
          <p className="text-muted">Yükleniyor...</p>
        </div>
      )}

      {stats && (
        <>
          <div className="grid-4 mb-24">
            <div className="stat-card">
              <div className="stat-card-value">{stats.overall.count.toLocaleString()}</div>
              <div className="stat-card-label">Toplam AI İşlemi</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-value">{formatDisplayedUsd(stats.overall.avg_usd)}</div>
              <div className="stat-card-label">Ort. USD / İşlem</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-value">{formatDisplayedUsd(stats.overall.total_usd)}</div>
              <div className="stat-card-label">Toplam LLM Maliyeti</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-value">{stats.overall.total_credits.toLocaleString()}</div>
              <div className="stat-card-label">Toplam Kredi Harcandı</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title">İşlem Türüne Göre</h2>
            </div>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>İşlem Türü</th>
                    <th style={{ textAlign: 'right' }}>Adet</th>
                    <th style={{ textAlign: 'right' }}>Ort. USD</th>
                    <th style={{ textAlign: 'right' }}>Toplam USD</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_usage_type.map((row) => (
                    <tr key={row.usage_type}>
                      <td>{USAGE_LABELS[row.usage_type] ?? row.usage_type}</td>
                      <td style={{ textAlign: 'right' }}>{row.count.toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{formatDisplayedUsd(row.avg_usd)}</td>
                      <td style={{ textAlign: 'right' }}>{formatDisplayedUsd(row.total_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}